import { useCallback, useEffect, useState } from 'react'

import { DataService } from 'api/DataService'

/**
 * Converte uma chave VAPID em base64 URL-safe (string) para Uint8Array,
 * formato exigido por `PushManager.subscribe({ applicationServerKey })`.
 */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = typeof window !== 'undefined' ? window.atob(base64) : ''
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

type Status = 'idle' | 'loading' | 'subscribed' | 'denied' | 'unsupported'

interface UsePushSubscriptionReturn {
  status: Status
  isSubscribed: boolean
  subscribe: () => Promise<boolean>
  unsubscribe: () => Promise<boolean>
}

/**
 * Gerencia a Web Push subscription do merchant_user autenticado.
 * Deve ser usado apenas dentro da área logada (subscription pertence ao merchant_user).
 */
export const usePushSubscription = (): UsePushSubscriptionReturn => {
  const [status, setStatus] = useState<Status>('idle')

  const vapidPublicKey = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY

  const isSupported = useCallback(() => {
    if (typeof window === 'undefined') return false
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    )
  }, [])

  useEffect(() => {
    if (!isSupported()) {
      setStatus('unsupported')
      return
    }

    if (Notification.permission === 'denied') {
      setStatus('denied')
      return
    }

    const checkExisting = async () => {
      try {
        const registration = await navigator.serviceWorker.ready
        const existing = await registration.pushManager.getSubscription()
        if (existing) setStatus('subscribed')
      } catch (error) {
        // ignore
      }
    }

    checkExisting()
  }, [isSupported])

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported()) {
      setStatus('unsupported')
      return false
    }
    if (!vapidPublicKey) {
      console.error('[push] NEXT_PUBLIC_VAPID_PUBLIC_KEY not configured')
      return false
    }

    setStatus('loading')

    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'idle')
        return false
      }

      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      })

      const json = subscription.toJSON()
      const response = await DataService({
        type: 'POST',
        url: '/v1/push/subscribe',
        data: {
          endpoint: json.endpoint,
          keys: {
            p256dh: json.keys?.p256dh,
            auth: json.keys?.auth
          }
        }
      })

      if (response?.data?.error) {
        setStatus('idle')
        return false
      }

      setStatus('subscribed')
      return true
    } catch (error) {
      console.error('[push] subscribe failed:', error)
      setStatus('idle')
      return false
    }
  }, [isSupported, vapidPublicKey])

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!isSupported()) return false

    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (!subscription) {
        setStatus('idle')
        return true
      }

      const endpoint = subscription.endpoint
      await subscription.unsubscribe()

      await DataService({
        type: 'DELETE',
        url: '/v1/push/subscribe',
        data: { endpoint }
      })

      setStatus('idle')
      return true
    } catch (error) {
      console.error('[push] unsubscribe failed:', error)
      return false
    }
  }, [isSupported])

  return {
    status,
    isSubscribed: status === 'subscribed',
    subscribe,
    unsubscribe
  }
}

export default usePushSubscription
